/**
 * `<AiTag>` — the text label that marks a user as an AI agent wherever a
 * name renders, like Slack's "APP".
 *
 * Text, never colour alone (the Reports `PersonRow` precedent): a hue or a
 * ring can't carry "this is not a person" for everyone. The edge is
 * `border-media-edge`, not `border-border`: the hairline tokens measure
 * 1.16:1 (Obsidian) / 1.20:1 (Midnight) against the surfaces a name sits on,
 * which left the tag as floating text with no pill around it. The visible text is
 * the short label; the accessible name is the full "AI agent".
 *
 * Pass the agent's `userId` and the model the agent runs on joins the
 * tooltip and accessible name ("AI agent, DeepSeek V4.1 Flash"); the visible
 * pill stays "AI" everywhere. The model is read from the shared
 * `/api/agent-models` cache; an unknown model falls back to the plain label.
 * Roomy surfaces that show an email as a second line show the model there
 * instead, via `<AgentModelLine>`.
 *
 * Callers key it on the `kind` the payload carries and never guess: pass
 * the user's `kind`, and the tag renders nothing for a human (or a payload
 * that predates `kind`). Avatar-only surfaces that have no room for a pill
 * use `useAiNameLabel` instead, which appends the label to the name for a
 * tooltip or `aria-label`.
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { UserKind } from '@/api/types-bridge'
import { useAgentModel, useAgentModels } from '@/features/agents/use-agent-models'
import { modelLabel } from '@/lib/model-label'

// The wire type is the single definition; re-exported so a caller that
// already imports the tag can name the type from the same place.
export type { UserKind }

export const isAgentKind = (kind: UserKind | string | null | undefined): boolean => kind === 'agent'

/**
 * The email a person should SEE for this user — `null` for an AI agent.
 *
 * An agent's address is a synthetic `agent-{uuid}@agents.3mote.com` that
 * exists only because Cognito and `users.email` require one; it is never
 * mailed and means nothing to a reader. Every surface that prints a user's
 * email (a secondary line, a tooltip, a search match) goes through this so
 * it can't leak one. Callers that need a non-empty fallback label for an
 * empty slot can use `common.aiTag.ariaLabel`.
 */
/** The domain the BE reserves for agents' synthetic addresses. */
const AGENT_EMAIL_SUFFIX = '@agents.3mote.com'

export const displayEmail = (user: {
  email?: string | null | undefined
  kind?: UserKind | string | null | undefined
}): string | null => {
  if (isAgentKind(user.kind)) return null
  const email = user.email ?? null
  // Backstop for a payload with no `kind` yet (a team-member row while the
  // tenant-users list is still loading): the reserved agent domain is never a
  // person's address, so it is never worth showing.
  if (email?.toLowerCase().endsWith(AGENT_EMAIL_SUFFIX)) return null
  return email
}

interface AiTagProps {
  kind: UserKind | string | null | undefined
  /** The agent's user id — puts the model it runs on in the tooltip and accessible name. */
  userId?: string | null | undefined
  className?: string
}

const PILL_CLASS =
  'inline-flex shrink-0 items-center rounded-sm border border-media-edge px-1 font-semibold text-[10px] text-fg-muted uppercase leading-4 tracking-wide'

export function AiTag({ kind, userId, className }: AiTagProps) {
  if (!isAgentKind(kind)) return null
  return userId ? (
    <AiTagWithModel userId={userId} className={className} />
  ) : (
    <AiTagPill label={null} className={className} />
  )
}

// Split from `AiTag` so the model lookup (a query) only runs for an agent
// whose id the caller supplied.
function AiTagWithModel({ userId, className }: { userId: string; className: string | undefined }) {
  const label = useAgentModelLabel(userId)
  return <AiTagPill label={label} className={className} />
}

function AiTagPill({ label, className }: { label: string | null; className: string | undefined }) {
  const { t } = useTranslation('common')
  return (
    <span
      role="img"
      aria-label={label ? t('aiTag.ariaLabelWithModel', { model: label }) : t('aiTag.ariaLabel')}
      title={label ?? undefined}
      className={`${PILL_CLASS}${className ? ` ${className}` : ''}`}
    >
      <span aria-hidden="true">{t('aiTag.label')}</span>
    </span>
  )
}

/** The readable model name for an agent, or `null` when unknown / not an agent. */
export function useAgentModelLabel(userId: string | null | undefined): string | null {
  const model = useAgentModel(userId)
  return model ? modelLabel(model) : null
}

/**
 * The agent's model on a line of its own, in the muted secondary-text style a
 * person's email uses. Only for surfaces with room for a second line (a row
 * that would otherwise show an email); renders nothing when the model is
 * unknown. Callers pass a person's email through `displayEmail`, which is
 * already null for an agent, so this takes its place.
 */
export function AgentModelLine({
  userId,
  className = 'truncate text-xs text-fg-muted',
}: {
  userId: string | null | undefined
  className?: string
}) {
  const label = useAgentModelLabel(userId)
  if (!label) return null
  // Hidden from screen readers: the AI tag beside the name already announces the model, so
  // reading the line too says it twice.
  return (
    <div title={label} aria-hidden="true" className={className}>
      {label}
    </div>
  )
}

/**
 * The name as a tooltip or accessible name should read it: "Ada · AI" for
 * an agent ("Ada · AI · DeepSeek V4.1 Flash" once the model is known when
 * the caller passes the agent's id), the bare name for anyone else.
 */
export function useAiNameLabel(): (
  name: string,
  kind: UserKind | string | null | undefined,
  userId?: string | null | undefined,
) => string {
  const { t } = useTranslation('common')
  const models = useAgentModels()
  // Stable between renders (changes only with the locale or the model map)
  // so callers can list it as a memo dependency.
  return useCallback(
    (name, kind, userId) => {
      if (!isAgentKind(kind)) return name
      const model = userId ? models.get(userId) : undefined
      return model
        ? t('aiTag.nameWithTagAndModel', { name, model: modelLabel(model) })
        : t('aiTag.nameWithTag', { name })
    },
    [t, models],
  )
}
